/** @format */

import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react'
import {
  Form,
  FormItem,
  Select,
  Option,
  Input,
  _$,
  Timepicker,
  Icon,
  Tooltip,
  OptionGroup
} from '@king-design/react'
import styled from 'styled-components'
import { TablePage } from 'src/components'
import { cloneDeep } from 'lodash'
import {
  TriggerRulesProps,
  periodOption,
  methodOption,
  unitOption,
  pointsOption,
  intervalOption,
  countOption,
  initRuleItem,
  withCustomRule,
  thanLastPeriod,
  getMetricUnitOptions,
  handleUnitsValue,
  bps_ops
} from './constants'
import ApiCommon from 'src/api/ApiCommon'
import { request } from 'src/utils'
import { useAsync } from 'react-use'

interface AlarmRuleProps {
  className?: string
  productType?: number
  triggerRules?: TriggerRulesProps[]
  setTriggerRules?: (value: TriggerRulesProps[]) => void
  hidden?: boolean
  required?: boolean
  readonly?: boolean
}
export interface AlarmRuleHandle {
  getTriggerRules: () => Promise<TriggerRulesProps[]>
}

const AlarmRule = forwardRef<AlarmRuleHandle, AlarmRuleProps>(
  (
    { className, triggerRules = [], setTriggerRules, productType, hidden, required, readonly },
    ref
  ) => {
    const formRef = useRef(null)
    const [metricGroup, setMetricGroup] = useState([])

    const { value: metricList } = useAsync(async () => {
      if (productType !== undefined) {
        const { data } = await request(ApiCommon.getProductMetricList, {
          data: { ProductType: productType, PageIndex: 1 }
        })
        if (data?.metricList?.length) {
          const result = []
          data?.metricList.forEach(
            ({ application, metricKey, metricName, units, delay, isDimension }) => {
              const key = application || _$('其它')
              const findIndex = result.findIndex(({ app }) => app === key)
              if (findIndex >= 0) {
                result[findIndex].children.push({
                  metricKey,
                  metricName,
                  units,
                  delay,
                  isDimension
                })
              } else {
                result.push({
                  app: key,
                  children: [{ metricKey, metricName, units, delay, isDimension }]
                })
              }
            }
          )
          setMetricGroup(result)
        }
        return data?.metricList || []
      }
      return []
    }, [productType])
    // 修改告警规则
    const changeRules = (index, value) => {
      const update = cloneDeep(triggerRules)
      update[index] = { ...update[index], ...value }
      setTriggerRules && setTriggerRules(update)
    }

    // 添加告警规则
    const addTriggerRules = async () => {
      const valid = await formRef.current.validate()
      if (valid) {
        const add = cloneDeep(triggerRules)
        add.push(initRuleItem)
        setTriggerRules && setTriggerRules(add)
      }
    }

    // 删除告警规则
    const removeTriggerRules = index => {
      const remove = cloneDeep(triggerRules)
      remove.splice(index, 1)
      setTriggerRules && setTriggerRules(remove)
    }

    useImperativeHandle(ref, () => ({
      getTriggerRules: async () => {
        const valid = await formRef.current.validate()
        return valid
          ? triggerRules.map(item => {
              const { itemKey, triggerValue, units, displayUnits } = item
              const unitOpts = getMetricUnitOptions(productType, itemKey, units)
              let value = triggerValue
              if (unitOpts?.length) {
                value = handleUnitsValue(triggerValue, displayUnits, units, unitOpts)
              } else if (units === 'bps') {
                value = handleUnitsValue(triggerValue, displayUnits, units, bps_ops)
              }
              return {
                ...item,
                triggerValue: value + ''
              }
            })
          : valid
      }
    }))
    const scheme: any = {
      itemName: {
        title: _$('监控项名称'),
        width: 300,
        fixed: 'left',
        template({ itemKey, itemName }, index) {
          return readonly ? (
            itemName
          ) : (
            <FormItem
              className="table-space"
              hideLabel={true}
              value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
              rules={{ required: true }}
            >
              <Select
                style={{ width: '255px' }}
                card
                value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
                filterable
                className="metrci_select"
                container=".select_position"
                placeholder={_$('选择监控项')}
                onChangeValue={value => {
                  const split = value.split('--')
                  const find = metricList?.find(
                    item => item.metricKey === split[0] && item.metricName === split[1]
                  )
                  const unitOpts = getMetricUnitOptions(productType, split[0], find?.units)
                  let initUnit = find.units
                  if (unitOpts?.length) {
                    if (unitOpts.includes('Mbps')) {
                      initUnit = 'Mbps'
                    }
                  } else if (find.units === 'bps') {
                    initUnit = 'Mbps'
                  }
                  if (find) {
                    changeRules(index, {
                      itemKey: find.metricKey,
                      itemName: find.metricName,
                      units: find.units,
                      displayUnits: initUnit
                    })
                  }
                }}
              >
                {metricGroup?.length
                  ? metricGroup.map(({ app, children }) => (
                      <OptionGroup label={app} key={app}>
                        {children.map(item => (
                          <Option
                            key={`${item.metricKey}`}
                            label={item.metricName}
                            value={`${item.metricKey}--${item.metricName}`}
                          >
                            <div className="metric_option">
                              <p>{item.metricName}</p>
                              <p className="metric_key">{item.metricKey}</p>
                            </div>
                          </Option>
                        ))}
                      </OptionGroup>
                    ))
                  : null}
              </Select>
              {itemName === '可用内存' || itemName === 'memory availability' ? (
                <Tooltip
                  content={_$(
                    // eslint-disable-next-line max-len
                    '从/proc/meminfo中得到MemAvailable，若/proc/meminfo中无MemAvailable，则MemAvailable=MemFree+Buffers+Cached'
                  )}
                >
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
              {itemName === '内存可用率' || itemName === 'available memory' ? (
                <Tooltip content={_$('可用内存/总内存*100%')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
            </FormItem>
          )
        }
      },
      period: {
        title: _$('统计周期'),
        width: 150,
        template({ itemKey, period, compare }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            periodOption.find(item => item.value === period)?.title
          ) : (
            <FormItem hideLabel={true} value={period || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={period || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    period: value
                  })
                }}
              >
                {periodOption?.length
                  ? periodOption.map(item => {
                      if (thanLastPeriod.includes(compare) && item.value === '60m') {
                        return null
                      } else {
                        return (
                          <Option key={item.key} value={item.value}>
                            {_$(item.title)}
                          </Option>
                        )
                      }
                    })
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      method: {
        title: _$('统计方法'),
        width: 150,
        template({ itemKey, method }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            methodOption.find(item => item.value === method)?.title
          ) : (
            <FormItem hideLabel={true} value={method || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={method || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    method: value
                  })
                }}
              >
                {methodOption?.length
                  ? methodOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      compare: {
        title: _$('大小'),
        width: 180,
        template({ itemName, itemKey, period, compare }, index) {
          if (withCustomRule.includes(itemKey) || itemKey?.includes('.nodata')) return ''
          return readonly ? (
            unitOption.find(item => item.value === compare)?.title
          ) : (
            <FormItem hideLabel={true} value={compare || ''} rules={{ required: true }}>
              <Select
                width={160}
                value={compare || ''}
                container=".select_position"
                onChangeValue={value => {
                  const units = thanLastPeriod.includes(value)
                    ? '%'
                    : metricList?.find(item => {
                        return item.metricKey === itemKey && item.metricName === itemName
                      })?.units || ''
                  // 大小为【周期环比】没有1小时选项
                  changeRules(index, {
                    compare: value,
                    units,
                    period: thanLastPeriod.includes(value) && period === '60m' ? '' : period,
                    displayUnits: thanLastPeriod.includes(value)
                      ? '%'
                      : units === 'bps'
                      ? 'Mbps'
                      : units
                  })
                }}
              >
                {unitOption?.length
                  ? unitOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      triggerValue: {
        title: _$('阈值'),
        width: 250,
        template({ itemKey, triggerValue = 0, units, compare, displayUnits }, index) {
          if (withCustomRule.includes(itemKey) || itemKey?.includes('.nodata')) return ''
          const unitOpts = getMetricUnitOptions(productType, itemKey, units)
          if (readonly) {
            return `${triggerValue}${displayUnits || units || ''}`
          }
          return (
            <div className="trigger_value_box">
              <FormItem
                className="table-space"
                hideLabel={true}
                value={triggerValue}
                rules={{ required: true, pointsLimit: true }}
              >
                <Input
                  onChangeValue={value =>
                    changeRules(index, {
                      triggerValue: value
                    })
                  }
                  width={itemKey === 'vpc.nat.ipconflict' ? 120 : 140}
                  value={triggerValue}
                  slotSuffix={
                    // 大小为【周期环比】单位替换成‘%’
                    thanLastPeriod.includes(compare) ? (
                      <React.Fragment>%</React.Fragment>
                    ) : units?.length <= 10 ? (
                      <React.Fragment>
                        {/* 单位进制 */}
                        {!unitOpts?.length ? displayUnits || units : null}
                      </React.Fragment>
                    ) : (
                      <Tooltip content={units}>
                        <Icon className="icon ion-help-circled" />
                      </Tooltip>
                    )
                  }
                />
                {itemKey === 'vpc.nat.ipconflict' ? (
                  // eslint-disable-next-line max-len
                  <Tooltip
                    content={_$(
                      '该计数为NAT时随机选择端口可能产生的冲突计数，仅当统计周期内，大量冲突计数产生，表明NAT端口资源紧张，建议阈值调整为每分钟200'
                    )}
                  >
                    <Icon className="icon ion-help-circled" />
                  </Tooltip>
                ) : null}
              </FormItem>
              {/* 单位进制 */}
              {unitOpts?.length && !thanLastPeriod.includes(compare) ? (
                <FormItem hideLabel={true} value={displayUnits || units} rules={{ required: true }}>
                  <Select
                    width={90}
                    value={displayUnits || units || ''}
                    container=".select_position"
                    onChangeValue={value => {
                      changeRules(index, {
                        displayUnits: value
                      })
                    }}
                  >
                    {unitOpts?.length
                      ? unitOpts.map(item => (
                          <Option key={item} value={item}>
                            {_$(item)}
                          </Option>
                        ))
                      : ''}
                  </Select>
                </FormItem>
              ) : null}
            </div>
          )
        }
      },
      effectBT: {
        title: _$('告警生效时间'),
        width: 160,
        template({ itemKey, effectBT, effectET }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            `${effectBT}-${effectET}`
          ) : (
            <FormItem
              hideLabel={true}
              value={effectBT && effectET ? [effectBT, effectET] : []}
              rules={{ required: true }}
            >
              <Timepicker
                style={{ width: '140px' }}
                value={[effectBT, effectET]}
                onChangeValue={time => {
                  changeRules(index, {
                    effectBT: time[0],
                    effectET: time[1]
                  })
                }}
                step="00:15:00"
                range
                valueFormat="HH:mm"
                showFormat="HH:mm"
              />
            </FormItem>
          )
        }
      },
      points: {
        title: (
          <span>
            {_$('持续周期数')}
            <Tooltip content={_$('连续几次报警的触发结果符合您设置的规则描述，才会触发报警')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ itemKey, period, points }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            points
          ) : (
            <FormItem hideLabel={true} value={points || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={points || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    points: value
                  })
                }}
              >
                {pointsOption?.length
                  ? pointsOption
                      .filter(({ value }) => value * parseInt(period) <= 60)
                      .map(item => (
                        <Option key={item.value} value={item.value}>
                          {_$(item.title)}
                        </Option>
                      ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      interval: {
        title: (
          <span>
            {_$('告警间隔')}
            <Tooltip content={_$('报警发生后如果未恢复正常，间隔多久后重复发送一次报警通知')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ interval }, index) {
          return readonly ? (
            interval
          ) : (
            <FormItem hideLabel={true} value={interval || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={interval || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    interval: value
                  })
                }}
              >
                {intervalOption?.length
                  ? intervalOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : null}
              </Select>
            </FormItem>
          )
        }
      },
      maxCount: {
        title: (
          <span>
            {_$('告警次数')}
            <Tooltip content="发送告警通知的最大次数">
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ maxCount }, index) {
          return readonly ? (
            `${maxCount}次`
          ) : (
            <FormItem hideLabel={true} value={maxCount || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={maxCount || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    maxCount: value
                  })
                }}
              >
                {countOption?.length
                  ? countOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                        {_$('次')}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      }
    }
    if (!readonly) {
      scheme.active = {
        title: _$('操作'),
        fixed: 'right',
        width: 100,
        template(_item, index) {
          return required && triggerRules?.length <= 1 ? (
            ''
          ) : (
            <a
              onClick={() => {
                removeTriggerRules(index)
              }}
            >
              {_$('删除')}
            </a>
          )
        }
      }
    }

    return (
      <div className={className} style={{ display: hidden ? 'none' : 'block' }}>
        {!readonly ? (
          <div className="alarm_rule_title">
            <p>{_$('满足任意条件可触发')}</p>
            <a
              onClick={() => {
                addTriggerRules()
              }}
            >
              <Icon className="ion-plus-round" />
              {_$('添加规则')}
            </a>
          </div>
        ) : null}
        <Form ref={formRef}>
          <TablePage
            tableProps={{
              checkType: 'none',
              data: triggerRules,
              scheme: scheme,
              resizable: true,
              rowKey: item => item.ruleId || `${item.itemKey}--${item.itemName}`
            }}
          ></TablePage>
        </Form>
      </div>
    )
  }
)

export default styled(AlarmRule)`
  .alarm_rule_title {
    display: flex;
    align-items: flex-end;
    & > * {
      margin-bottom: 0 !important;
      margin-right: 8px;
    }
    .create_model_title {
      padding: 0 !important;
      margin-top: 0 !important;
      border-top: none !important;
    }
    p {
      color: #999;
      line-height: 12px;
      font-size: 12px;
    }
    a {
      line-height: 12px;
      font-size: 12px;
      .k-icon {
        color: #0091ea;
        font-size: 14px;
        margin-right: 4px;
      }
    }
  }
  .k-table {
    margin-top: 16px;
    .ion-help-circled {
      font-size: 14px;
      margin-left: 4px;
    }
    .table-space {
      margin: 0;
      .k-content > * {
        margin-right: 4px;
      }
    }
    .k-form-item .k-error {
      position: static;
      .c-ellipsis {
        white-space: normal;
      }
    }
    .trigger_value_box {
      display: flex;
      align-items: center;
    }
  }
`
